/**
 * @fileoverview Authentication context provider using Supabase
 * @module contexts/AuthContext
 *
 * USAGE:
 * import { useAuth } from './contexts/AuthContext';
 * const { user, signIn, signOut } = useAuth();
 *
 * DEPENDENCIES:
 * - @supabase/supabase-js
 * - ../lib/logger
 */

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '../config/supabase';
import { AUTH_BYPASS, BYPASS_USER } from '../config/authBypass';
import logger from '../lib/logger';
import apiClient from '../lib/apiClient';
import {
  authRedirectOrigin,
  isNative,
  NATIVE_AUTH_REDIRECT,
  openOAuthAndAwaitCallback,
} from '../platform';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth and listen for changes
  useEffect(() => {
    // Dev-only bypass (see config/authBypass.js): hand out the synthetic
    // guest user instead of consulting Supabase. Without this, everything
    // gated on `user` (MainApp's departments fetch, for one) waits forever
    // and the shell never gets past "Loading departments…".
    if (AUTH_BYPASS) {
      logger.auth.warn('AUTH BYPASS active — synthetic guest user, no session/token');
      setUser(BYPASS_USER);
      setSession(null);
      setLoading(false);
      return undefined;
    }

    let mounted = true;

    const initAuth = async () => {
      try {
        // Get initial session
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          logger.auth.error('Session error', null, error);
        }
        
        if (mounted) {
          setSession(session);
          setUser(session?.user ?? null);
        }
      } catch (err) {
        logger.auth.error('Auth initialization error', null, err);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initAuth();

    // Listen for auth changes.
    //
    // IMPORTANT: Supabase fires TOKEN_REFRESHED on tab-focus and on the
    // refresh-interval. Naively calling `setUser(session?.user)` creates
    // a new user object reference every time, which re-runs every
    // downstream `useEffect(..., [user])` and causes the whole page to
    // feel like it's reloading when the user just tabbed back.
    //
    // We keep user reference stable unless user.id actually changed;
    // session updates freely (access_token rotates on refresh, and
    // apiClient needs the fresh one).
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        logger.auth.sessionEvent(event, !!session);

        if (mounted) {
          setSession(session);
          setUser(prev => {
            const next = session?.user ?? null;
            if (prev?.id === next?.id) return prev; // stable ref on TOKEN_REFRESHED
            return next;
          });
          setLoading(false);
        }

        // Handle specific events
        if (event === 'SIGNED_IN') {
          logger.auth.signIn('session', true);
          // Record login time in backend (fire-and-forget, don't block auth flow)
          apiClient.post('/api/auth/record-login')
            .then(() => logger.auth.info('Login time recorded'))
            .catch(err => logger.auth.warn('Failed to record login time', { error: err.message }));
        } else if (event === 'SIGNED_OUT') {
          logger.auth.signOut();
        } else if (event === 'TOKEN_REFRESHED') {
          logger.auth.tokenRefresh(true);
        } else if (event === 'USER_UPDATED') {
          logger.auth.info('User data updated');
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Sign in with email/password
  const signIn = async (email, password) => {
    try {
      setError(null);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Sign up with email/password
  const signUp = async (email, password, metadata = {}) => {
    try {
      setError(null);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
          // Emailed link — must return to the website, not capacitor://localhost
          emailRedirectTo: `${authRedirectOrigin()}/auth/callback`
        }
      });

      if (error) throw error;
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Sign in with OAuth provider (Google, Apple, GitHub).
  //
  // Web: hand the browser to the provider and let /auth/callback finish it.
  // Native: Supabase gives us the authorize URL, we present it in Safari and
  // exchange the code ourselves when the custom scheme wakes the app back up
  // (see platform/openOAuthAndAwaitCallback).
  const signInWithProvider = async (provider) => {
    try {
      setError(null);

      // Clear any stale PKCE verifiers
      const storageKeys = Object.keys(localStorage);
      storageKeys.forEach(key => {
        if (key.includes('supabase.auth.pkce')) {
          localStorage.removeItem(key);
          logger.auth.debug('Cleared stale PKCE key', { key });
        }
      });

      logger.auth.info(`Initiating ${provider} OAuth`, { native: isNative });

      // access_type/prompt are Google-specific. Apple rejects an authorize
      // request carrying parameters it doesn't recognise, so scope them.
      const queryParams = provider === 'google'
        ? { access_type: 'offline', prompt: 'consent' }
        : undefined;

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: isNative
            ? NATIVE_AUTH_REDIRECT
            : `${window.location.origin}/auth/callback`,
          skipBrowserRedirect: isNative,
          ...(queryParams ? { queryParams } : {})
        }
      });

      if (error) {
        logger.auth.error(`OAuth error for ${provider}`, { provider }, error);
        throw error;
      }

      if (!isNative) {
        // OAuth redirects the browser; /auth/callback picks it up from here.
        logger.auth.info('OAuth initiated, redirecting to provider');
        return data;
      }

      if (!data?.url) throw new Error('Supabase returned no authorize URL');

      const callbackUrl = await openOAuthAndAwaitCallback(data.url);

      // The provider can hand back an error instead of a code (user tapped
      // Cancel on Apple's sheet, consent declined, …).
      const params = new URLSearchParams(callbackUrl.split('?')[1] || '');
      const oauthError = params.get('error_description') || params.get('error');
      if (oauthError) throw new Error(oauthError);

      const code = params.get('code');
      if (!code) throw new Error('No authorization code in callback');

      const { data: exchanged, error: exchangeError } =
        await supabase.auth.exchangeCodeForSession(code);
      if (exchangeError) throw exchangeError;

      logger.auth.info('Native OAuth completed', { provider });
      return exchanged;
    } catch (err) {
      logger.auth.error('OAuth error', { provider }, err);
      setError(err.message || `Failed to sign in with ${provider}`);
      throw err;
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    try {
      setError(null);
      // Native shell: window.location.origin is capacitor://localhost, which
      // Supabase can't redirect to — it falls back to the Site URL and the
      // user lands logged-in with no reset screen. Auth emails always return
      // to the real website (see platform/authRedirectOrigin).
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${authRedirectOrigin()}/reset-password`
      });

      if (error) throw error;
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Update password
  const updatePassword = async (newPassword) => {
    try {
      setError(null);
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      setError(null);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      // Clear all auth-related storage
      const storageKeys = Object.keys(localStorage);
      storageKeys.forEach(key => {
        if (key.includes('supabase.auth')) {
          localStorage.removeItem(key);
        }
      });
      
      setUser(null);
      setSession(null);
    } catch (err) {
      logger.auth.error('Sign out error', null, err);
      setError(err.message);
      // Clear state even if sign out fails
      setUser(null);
      setSession(null);
    }
  };

  // Refresh session
  const refreshSession = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.refreshSession();
      if (error) throw error;
      
      setSession(session);
      setUser(session?.user ?? null);
      return session;
    } catch (err) {
      logger.auth.error('Session refresh error', null, err);
      setError(err.message);
      throw err;
    }
  };

  // Get current session
  const getSession = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session;
  };

  // Memoize so the context value only changes when state actually changes.
  // Without this, every AuthProvider render produces a new value object and
  // every consumer re-renders — even if user/session/loading/error are the same.
  const value = useMemo(() => ({
    user,
    session,
    loading,
    error,
    signIn,
    signUp,
    signInWithProvider,
    signInWithGoogle: () => signInWithProvider('google'),
    signInWithApple: () => signInWithProvider('apple'),
    signInWithGithub: () => signInWithProvider('github'),
    signOut,
    resetPassword,
    updatePassword,
    refreshSession,
    getSession,
    clearError: () => setError(null),
    supabase,
  }), [user, session, loading, error]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};